import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
} from 'react';
import { FeaturedReview } from 'rwl-shared/dist/models';
import { useContentLists } from '../ContentProvider';
import { useFetchReview } from './useFetchReview';

export interface ReviewContextProps {
    review: FeaturedReview | undefined;
    setReviewId: (reviewId: string | undefined) => void;
    clearReview: () => void;
    isLoading: boolean;
}

const ReviewContext = createContext<ReviewContextProps | undefined>(undefined);

export const ReviewProvider = ({ children }: { children: React.ReactNode }) => {
    const { allReviewsList } = useContentLists();
    const [reviewId, setReviewId] = useState<string | undefined>();
    const [review, setReview] = useState<FeaturedReview | undefined>();
    const [isLoading, setIsLoading] = useState(false);

    const { data: fetchedReview, isLoading: fetchLoading } = useFetchReview({
        reviewId,
        enabled: !review,
    });

    const clearReview = useCallback(() => {
        setReviewId(undefined);
        setReview(undefined);
    }, []);

    const findExistingReview = (reviewId: string | undefined) =>
        allReviewsList.find((review) => review.id === reviewId);

    useEffect(() => {
        // Check if review already exists in the review list
        const existingReview = findExistingReview(reviewId);
        if (existingReview) {
            setIsLoading(false);
        } else {
            setIsLoading(true);
        }
    }, [reviewId, allReviewsList]);

    useEffect(() => {
        setReview(fetchedReview);
        setIsLoading(false);
    }, [fetchedReview]);

    return (
        <ReviewContext.Provider
            value={{
                review,
                setReviewId: (reviewId: string | undefined) => {
                    clearReview();
                    const existingReview = findExistingReview(reviewId);
                    if (existingReview) {
                        setReview(existingReview);
                    }
                    setReviewId(reviewId);
                },
                clearReview,
                isLoading: fetchLoading || isLoading,
            }}
        >
            {children}
        </ReviewContext.Provider>
    );
};

export const useReview = () => {
    const context = useContext(ReviewContext);
    if (!context) {
        throw new Error('useReview must be used within a ReviewProvider');
    }
    return context;
};
